import { createLogger, addTransport, installConsoleInterception } from './core/logging/logger.js';
import { createRemoteTransport } from './core/logging/remote-transport.js';
import { createEventBus } from './core/events/event-bus.js';
import { EVENT_REGISTRY } from './core/events/events.js';
import { createApiClient } from './core/http/api-client.js';
import { createUploadClient } from './core/http/upload-client.js';
import { createRouter } from './core/routing/router.js';
import { createPageRegistry } from './core/registry/page-registry.js';
import { createAccessResolver } from './core/auth/access-resolver.js';
import { createAuthService } from './core/auth/auth-service.js';
import { createContextManager } from './core/context/context-manager.js';
import { createMockMenuAdapter } from './adapters/mock-menu.js';
import { createMockAccessAdapter } from './adapters/mock-access.js';
import mountUsersPage from './pages/users.js';
import mountRegionsPage from './pages/regions.js';
import mountLanguagesPage from './pages/languages.js';
import mountCurrenciesPage from './pages/currencies.js';
import mountAuthorTypesPage from './pages/author-types.js';
import mountCompanyFormsPage from './pages/company-forms.js';
import mountFbSavedFormsPage from './pages/fb-savedforms.js';
import mountCompanySectorsPage from './pages/company-sectors.js';
import mountClientsTypePage from './pages/clients-type.js';
import mountBannersTypesPage from './pages/banners-types.js';
import mountBannersClientsPage from './pages/banners-clients.js';
import mountWorkWhoPage from './pages/work-who.js';
import mountPrivilegesPage from './pages/privileges.js';
import mountRegLevelsPage from './pages/reg-levels.js';
import mountExpoContinentsPage from './pages/expo-continents.js';
import mountTextOtherVarsPage from './pages/text-other-vars.js';
import mountTranslationsVarsPage from './pages/translations-vars.js';
import mountModulesPage from './pages/modules.js';
import mountObjectTypesPage from './pages/itemserver-object-types.js';
import mountObjectSourcesPage from './pages/itemserver-object-sources.js';
import mountObjectLocationsPage from './pages/itemserver-object-locations.js';
import mountBusinessTypeClassificationPage from './pages/business-type-classification.js';
import mountModulesCountriesPage from './pages/modules-countries.js';
import mountDictPage from './pages/dict.js';
import mountShopDiscountCodesPage from './pages/shop-discount-codes.js';
import mountLocaleConfigPage from './pages/locale-config.js';
import mountCompanyTypePage from './pages/company-type.js';
import mountArticleSuperiorsPage from './pages/article-superiors.js';
import mountCountryArticletypePage from './pages/country-articletype.js';
import mountArticleSectionsPage from './pages/article-sections.js';
import mountArticletypePage from './pages/articletype.js';
import mountArticletypeSuperiorPage from './pages/articletype-superior.js';
import mountContactsAccessPage from './pages/contacts-access.js';
import mountPostRegionsPage from './pages/post-regions.js';
import mountPostCountriesPage from './pages/post-countries.js';
import mountOrderDetailsPage from './pages/order-details.js';
import mountOrderCardProviderPage from './pages/order-card-provider.js';
import mountCompanyShortDescPage from './pages/company-short-description.js';
import mountNewsletterPage from './pages/newsletter.js';
import mountBookMagEditionsPage from './pages/book-magazine-editions.js';
import mountImagePluginsPage from './pages/itemserver-image-plugins.js';
import mountRouterOtherLinksPage from './pages/router-other-links.js';
import mountContactsRolesPage from './pages/contacts-roles.js';
import mountUsersRolesPage from './pages/users-roles.js';
import mountMenuAccessPage from './pages/menu-access.js';
import mountExpoCountriesPage from './pages/expo-countries.js';
import mountKatalogPage from './pages/katalog.js';
import mountTextOtherPage from './pages/text-other.js';
import mountRegInfoPage from './pages/reg-info.js';
import mountCompanyOfferTypesPage from './pages/company-offer-types.js';
import mountExpoRegionsPage from './pages/expo-regions.js';
import mountNavMenuPage from './pages/nav-menu.js';
import mountMenuPage from './pages/menu.js';
import mountMenuClientPage from './pages/menu-client.js';
import mountIconsPage from './pages/icons.js';
import mountAdvertorialPage from './pages/advertorial.js';
import mountArticlePermanentPage from './pages/article-permanent.js';
import mountFeaturedPage from './pages/featured.js';
import mountForumPage from './pages/forum.js';
import mountForumEmailPage from './pages/forumemail.js';
import mountForumThemesPage from './pages/forum-themes.js';
import mountUsersAdminPage from './pages/users-admin.js';
import mountUsersAccessPage from './pages/users-access.js';
import mountRegPage from './pages/reg.js';
import mountBookTypesPage from './pages/book-types.js';
import mountAuthorsPage from './pages/authors.js';
import mountBooksPage from './pages/books.js';
import mountVideosPage from './pages/videos.js';
import mountMailingContentPage from './pages/mailing-content.js';
import mountTranslationsPage from './pages/translations.js';
import mountBtcTranslationsPage from './pages/btc-translations.js';
import mountWorkPage from './pages/work.js';
import mountExpoTranslationsPage from './pages/expo-translations.js';
import mountCountriesPage from './pages/countries.js';
import mountBannersPage from './pages/banners.js';
import mountBannersGridPage from './pages/banners-grid.js';
import mountBannersOrderPage from './pages/banners-order.js';
import mountOrderPage from './pages/order.js';
import mountNewsletterDraftPage from './pages/newsletter-draft.js';
import mountForumAllPage from './pages/forum-all.js';
import mountExpoMainPage from './pages/expo-main.js';
import mountExpoPage from './pages/expo.js';
import mountContactsPage from './pages/contacts.js';
import mountClientsPage from './pages/clients.js';
import mountArticlePage from './pages/article.js';
import mountCompanyPage from './pages/company.js';
import { mountFileManagerPage } from './pages/file-manager.js';
import { createRestMediaClient } from './adapters/rest-media-client.js';
import { createRestFileManagerClient } from './adapters/rest-file-manager-client.js';
import { createRestCardGridLayoutStore } from './adapters/rest-card-grid-layout-store.js';
import { mountDashboardPage } from './pages/dashboard.js';
import { createFileMetadataFromUpload } from './core/media/file-metadata.js';
import { openFilePicker as openFilePickerImpl } from './features/file-browser/file-picker-modal.js';
import { openFormBuilderModal } from './pages/fb-forms/form-builder-modal.js';
import { bootstrapApp } from './bootstrap.js';
import { createLocaleLoader, loadLocaleForBoot } from './core/locale/locale-loader.js';

// ---------------------------------------------------------------------------
// Shared infrastructure
// ---------------------------------------------------------------------------

const logger = createLogger({ module: 'app' });

const remoteLog = createRemoteTransport({ url: '/api/log.php' });
addTransport(remoteLog.transport);
installConsoleInterception();

const eventBus = createEventBus(EVENT_REGISTRY);

// ---------------------------------------------------------------------------
// Auth (frontend mock — swap `verifyCredentials` for a real `/api/login` call
// later; the service, view, and gate stay unchanged). The verifier returns the
// whole session; the service persists it and never mints tokens.
// ---------------------------------------------------------------------------

const MOCK_SESSION_TOKEN = 'mock-session-token';

/** @type {import('./contracts/auth.js').VerifyFn} */
// `signal` is accepted for contract parity with the real verifier; the mock is
// synchronous (no request to abort), so it is intentionally unused here.
const verifyCredentials = async (email, password) =>
  email.trim().toLowerCase() === 'admin@franchising.pl' && password === 'franchising'
    ? {
        user: { id: '1', email: 'admin@franchising.pl', name: 'Administrator', roles: ['admin'], permissions: [] },
        token: MOCK_SESSION_TOKEN,
      }
    : null;

const authService = createAuthService({
  verify: verifyCredentials,
  logger: createLogger({ module: 'auth-service' }),
});

// Reads the live session token so the later API step already sends the real
// Bearer. Empty string → api-client skips the Authorization header.
const getToken = () => Promise.resolve(authService.getState().token ?? '');

const apiClient = createApiClient({
  baseUrl: '/api',
  getToken,
});

const uploadClient = createUploadClient({
  baseUrl: '/api',
  getToken,
});

// Narrow media client passed globally (lights up the rich-text image picker).
const mediaClient = createRestMediaClient({ apiClient });
// Broad admin client — injected ONLY into the file-manager page closure below.
const fileManagerClient = createRestFileManagerClient({ apiClient, logger: createLogger({ module: 'file-manager-client' }) });
// Per-user dashboard layout store — injected into the dashboard page closure.
const dashboardLayoutStore = createRestCardGridLayoutStore({ apiClient, endpoint: '/dashboard/layout', logger: createLogger({ module: 'dashboard-layout-store' }) });
// Narrow browse client for the in-editor file-field picker — list + a
// FileMeta-returning persistUpload ONLY (no admin remove/tag powers). Wraps
// fileManagerClient at the boundary so the editor never receives the admin surface.
/** @type {import('./contracts/file-selection.js').FileBrowseClient} */
const fileBrowseClient = {
  list: (q, opts) => fileManagerClient.list(q, opts),
  persistUpload: (uploaded, file, opts) =>
    fileManagerClient.create(createFileMetadataFromUpload(uploaded, file), opts),
};

/** @type {import('./contracts/i18n.js').Translator} */
const t = (key) => key;

const access = createAccessResolver({ roles: [], userOverrides: { additions: [], removals: [] } });
const pageRegistry = createPageRegistry();
const router = createRouter({ defaultRoute: '/dashboard' });

// ---------------------------------------------------------------------------
// Mock adapters
// ---------------------------------------------------------------------------

const menuAdapter = createMockMenuAdapter({ apiClient });
const accessAdapter = createMockAccessAdapter();

// ---------------------------------------------------------------------------
// Page registration
// ---------------------------------------------------------------------------

pageRegistry.register({
  key: 'users',
  label: 'Users',
  permission: 'users.view',
  mount: mountUsersPage,
});

// File manager — 2-arg PageMountFn closure injects the broad fileManagerClient
// (keeps the admin create/remove/tag surface out of the global PageServices; the
// editor receives only the NARROW filePicker capability ({ browse, open }) +
// mediaClient — never the admin client).
pageRegistry.register({
  key: 'file-manager',
  label: 'File manager',
  mount: (fmContainer, fmServices) =>
    mountFileManagerPage(fmContainer, { ...fmServices, fileManagerClient }),
});

// Dashboard — 2-arg closure injects the layout store + the app router's navigate
// (keeps the stable PageServices contract unchanged; cards reach navigation via
// the injected navigate, never a raw hash write).
pageRegistry.register({
  key: 'dashboard',
  label: 'Dashboard',
  mount: (dashContainer, dashServices) =>
    mountDashboardPage(dashContainer, { ...dashServices, dashboardLayoutStore, navigate: router.navigate }),
});

pageRegistry.register({ key: 'regions', label: 'Regions', mount: mountRegionsPage });
pageRegistry.register({ key: 'languages', label: 'Languages', mount: mountLanguagesPage });
pageRegistry.register({ key: 'currencies', label: 'Currencies', mount: mountCurrenciesPage });
pageRegistry.register({ key: 'author-types', label: 'Author Types', mount: mountAuthorTypesPage });
pageRegistry.register({ key: 'company-forms', label: 'Company Forms', mount: mountCompanyFormsPage });
pageRegistry.register({ key: 'fb-savedforms', label: 'Form Builder', mount: mountFbSavedFormsPage });
pageRegistry.register({ key: 'company-sectors', label: 'Company Sectors', mount: mountCompanySectorsPage });
pageRegistry.register({ key: 'clients-type', label: 'Client Types', mount: mountClientsTypePage });
pageRegistry.register({ key: 'banners-types', label: 'Banner Types', mount: mountBannersTypesPage });
pageRegistry.register({ key: 'banners-clients', label: 'Banner Clients', mount: mountBannersClientsPage });
pageRegistry.register({ key: 'work-who', label: 'Work Who', mount: mountWorkWhoPage });
pageRegistry.register({ key: 'privileges', label: 'Privileges', mount: mountPrivilegesPage });
pageRegistry.register({ key: 'reg-levels', label: 'Registration Levels', mount: mountRegLevelsPage });
pageRegistry.register({ key: 'expo-continents', label: 'Expo Continents', mount: mountExpoContinentsPage });
pageRegistry.register({ key: 'text-other-vars', label: 'Text Other Variables', mount: mountTextOtherVarsPage });
pageRegistry.register({ key: 'translations-vars', label: 'Translation Variables', mount: mountTranslationsVarsPage });
pageRegistry.register({ key: 'modules', label: 'Modules', mount: mountModulesPage });
pageRegistry.register({ key: 'itemserver-object-types', label: 'Object Types', mount: mountObjectTypesPage });
pageRegistry.register({ key: 'itemserver-object-sources', label: 'Object Sources', mount: mountObjectSourcesPage });
pageRegistry.register({ key: 'itemserver-object-locations', label: 'Object Locations', mount: mountObjectLocationsPage });
pageRegistry.register({ key: 'business-type-classification', label: 'Business Type Classification', mount: mountBusinessTypeClassificationPage });
pageRegistry.register({ key: 'modules-countries', label: 'Modules Countries', mount: mountModulesCountriesPage });
pageRegistry.register({ key: 'dict', label: 'Dictionary', mount: mountDictPage });
pageRegistry.register({ key: 'shop-discount-codes', label: 'Discount Codes', mount: mountShopDiscountCodesPage });
pageRegistry.register({ key: 'locale-config', label: 'Locale Config', mount: mountLocaleConfigPage });
pageRegistry.register({ key: 'company-type', label: 'Company Types', mount: mountCompanyTypePage });
pageRegistry.register({ key: 'article-superiors', label: 'Article Superiors', mount: mountArticleSuperiorsPage });
pageRegistry.register({ key: 'country-articletype', label: 'Country Article Types', mount: mountCountryArticletypePage });
pageRegistry.register({ key: 'article-sections', label: 'Article Sections', mount: mountArticleSectionsPage });
pageRegistry.register({ key: 'articletype', label: 'Article Types', mount: mountArticletypePage });
pageRegistry.register({ key: 'articletype-superior', label: 'Article Type Superiors', mount: mountArticletypeSuperiorPage });
pageRegistry.register({ key: 'contacts-access', label: 'Contacts Access', mount: mountContactsAccessPage });
pageRegistry.register({ key: 'post-regions', label: 'Post Regions', mount: mountPostRegionsPage });
pageRegistry.register({ key: 'post-countries', label: 'Post Countries', mount: mountPostCountriesPage });
pageRegistry.register({ key: 'order-details', label: 'Order Details', mount: mountOrderDetailsPage });
pageRegistry.register({ key: 'order-card-provider', label: 'Order Card Providers', mount: mountOrderCardProviderPage });
pageRegistry.register({ key: 'company-short-description', label: 'Company Short Descriptions', mount: mountCompanyShortDescPage });
pageRegistry.register({ key: 'newsletter', label: 'Newsletter', mount: mountNewsletterPage });
pageRegistry.register({ key: 'book-magazine-editions', label: 'Book Magazine Editions', mount: mountBookMagEditionsPage });
pageRegistry.register({ key: 'itemserver-image-plugins', label: 'Image Plugins', mount: mountImagePluginsPage });
pageRegistry.register({ key: 'router-other-links', label: 'Router Other Links', mount: mountRouterOtherLinksPage });
pageRegistry.register({ key: 'contacts-roles', label: 'Contact Roles', mount: mountContactsRolesPage });
pageRegistry.register({ key: 'users-roles', label: 'User Roles', mount: mountUsersRolesPage });
pageRegistry.register({ key: 'menu-access', label: 'Menu Access', mount: mountMenuAccessPage });
pageRegistry.register({ key: 'expo-countries', label: 'Expo Countries', mount: mountExpoCountriesPage });
pageRegistry.register({ key: 'katalog', label: 'Katalog', mount: mountKatalogPage });
pageRegistry.register({ key: 'text-other', label: 'Other Texts', mount: mountTextOtherPage });
pageRegistry.register({ key: 'reg-info', label: 'Registration Info', mount: mountRegInfoPage });
pageRegistry.register({ key: 'company-offer-types', label: 'Company Offer Types', mount: mountCompanyOfferTypesPage });
pageRegistry.register({ key: 'expo-regions', label: 'Expo Regions', mount: mountExpoRegionsPage });
pageRegistry.register({ key: 'nav-menu', label: 'Navigation Menu', mount: mountNavMenuPage });
pageRegistry.register({ key: 'menu', label: 'Menu', mount: mountMenuPage });
pageRegistry.register({ key: 'menu-client', label: 'Client Menu', mount: mountMenuClientPage });
pageRegistry.register({ key: 'icons', label: 'Icons', mount: mountIconsPage });
pageRegistry.register({ key: 'advertorial', label: 'Advertorial', mount: mountAdvertorialPage });
pageRegistry.register({ key: 'article-permanent', label: 'Permanent Articles', mount: mountArticlePermanentPage });
pageRegistry.register({ key: 'featured', label: 'Featured', mount: mountFeaturedPage });
pageRegistry.register({ key: 'forum', label: 'Forum', mount: mountForumPage });
pageRegistry.register({ key: 'forumemail', label: 'Forum Email', mount: mountForumEmailPage });
pageRegistry.register({ key: 'forum-themes', label: 'Forum Themes', mount: mountForumThemesPage });
pageRegistry.register({ key: 'users-admin', label: 'Users', mount: mountUsersAdminPage });
pageRegistry.register({ key: 'users-access', label: 'Users Access', mount: mountUsersAccessPage });
pageRegistry.register({ key: 'reg', label: 'Registrations', mount: mountRegPage });
pageRegistry.register({ key: 'book-types', label: 'Book Types', mount: mountBookTypesPage });
pageRegistry.register({ key: 'authors', label: 'Authors', mount: mountAuthorsPage });
pageRegistry.register({ key: 'books', label: 'Books & Magazines', mount: mountBooksPage });
pageRegistry.register({ key: 'videos', label: 'Videos', mount: mountVideosPage });
pageRegistry.register({ key: 'mailing-content', label: 'Mailing Content', mount: mountMailingContentPage });
pageRegistry.register({ key: 'translations', label: 'Translations', mount: mountTranslationsPage });
pageRegistry.register({ key: 'btc-translations', label: 'BTC Translations', mount: mountBtcTranslationsPage });
pageRegistry.register({ key: 'work', label: 'Contacts', mount: mountWorkPage });
pageRegistry.register({ key: 'expo-translations', label: 'Expo Translations', mount: mountExpoTranslationsPage });
pageRegistry.register({ key: 'countries', label: 'Countries', mount: mountCountriesPage });
pageRegistry.register({ key: 'banners', label: 'Banners', mount: mountBannersPage });
pageRegistry.register({ key: 'banners-grid', label: 'Banners (Grid)', mount: mountBannersGridPage });
pageRegistry.register({ key: 'banners-order', label: 'Banners Order', mount: mountBannersOrderPage });
pageRegistry.register({ key: 'order', label: 'Orders', mount: mountOrderPage });
pageRegistry.register({ key: 'newsletter-draft', label: 'Newsletter Drafts', mount: mountNewsletterDraftPage });
pageRegistry.register({ key: 'forum-all', label: 'Forum (All)', mount: mountForumAllPage });
pageRegistry.register({ key: 'expo-main', label: 'Expos (Main)', mount: mountExpoMainPage });
pageRegistry.register({ key: 'expo', label: 'Expos (Events)', mount: mountExpoPage });
pageRegistry.register({ key: 'contacts', label: 'Contacts', mount: mountContactsPage });
pageRegistry.register({ key: 'clients', label: 'Clients', mount: mountClientsPage });
pageRegistry.register({ key: 'article', label: 'Articles', mount: mountArticlePage });
pageRegistry.register({ key: 'company', label: 'Companies', mount: mountCompanyPage });

// ---------------------------------------------------------------------------
// Context options
// ---------------------------------------------------------------------------

/** @type {Array<{ country: string, lang: string, label: string }>} */
const contextOptions = [
  { country: 'dz', lang: 'ar', label: 'Algeria' },
  { country: 'ar', lang: 'es', label: 'Argentina' },
  { country: 'bh', lang: 'ar', label: 'Bahrain' },
  { country: 'ba', lang: 'ba', label: 'Bosnia and Hercegovina' },
  { country: 'br', lang: 'pt', label: 'Brazil' },
  { country: 'hr', lang: 'hr', label: 'Croatia' },
  { country: 'cz', lang: 'cs', label: 'Czech Republic' },
  { country: 'eg', lang: 'ar', label: 'Egypt' },
  { country: 'eu', lang: 'en', label: 'Europe (in English)' },
  { country: 'hu', lang: 'hu', label: 'Hungary' },
  { country: 'iq', lang: 'ar', label: 'Iraq' },
  { country: 'it', lang: 'it', label: 'Italy' },
  { country: 'jo', lang: 'ar', label: 'Jordan' },
  { country: 'kw', lang: 'ar', label: 'Kuwait' },
  { country: 'lv', lang: 'lv', label: 'Latvia' },
  { country: 'lt', lang: 'lt', label: 'Lithuania' },
  { country: 'ma', lang: 'ar', label: 'Morocco' },
  { country: 'mk', lang: 'mk', label: 'North Macedonia' },
  { country: 'om', lang: 'ar', label: 'Oman' },
  { country: 'pl', lang: 'pl', label: 'Poland' },
  { country: 'qa', lang: 'ar', label: 'Qatar' },
  { country: 'ro', lang: 'ro', label: 'Romania' },
  { country: 'ru', lang: 'ru', label: 'Russia' },
  { country: 'sa', lang: 'ar', label: 'Saudi Arabia' },
  { country: 'sr', lang: 'sr', label: 'Serbia' },
  { country: 'sk', lang: 'sk', label: 'Slovakia' },
  { country: 'si', lang: 'sl', label: 'Slovenia' },
  { country: 'tn', lang: 'ar', label: 'Tunisia' },
  { country: 'tr', lang: 'tr', label: 'Turkey' },
  { country: 'ua_ru', lang: 'ru', label: 'Ukraine (in Russian)' },
  { country: 'ua', lang: 'uk', label: 'Ukraine (in Ukrainian)' },
  { country: 'ae', lang: 'ar', label: 'United Arab Emirates' },
];

// Validate a stored context against the current option set, so a returning user
// with a legacy value (e.g. the old 'rs'/'sr-Latn') falls back to the default
// instead of leaving the switcher blank with an unresolvable context.
const validContextKeys = new Set(contextOptions.map((o) => `${o.country}|${o.lang}`));
const contextManager = createContextManager({
  isValid: (ctx) => validContextKeys.has(`${ctx.country}|${ctx.lang}`),
});

// ---------------------------------------------------------------------------
// Available designs
// ---------------------------------------------------------------------------

// Must stay a literal array for scripts/check-theme-structure.js parsing:
const AVAILABLE_DESIGNS = ['default', 'material'];

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

// Hard ceiling — admin UI must not hang on a config endpoint. The helper
// resolves with LOCALE_DEFAULTS on timeout, abort, or any loader failure.
const LOCALE_BOOT_TIMEOUT_MS = 1500;

(async () => {
  const localeConfig = await loadLocaleForBoot(
    // Pass the app logger so the suspicious-token warning (e.g. dateFormat
    // contains a stand-alone time character like "HH:mm") surfaces at boot
    // through the structured logger AND remote transport, not just in tests.
    createLocaleLoader({ apiClient, logger }),
    LOCALE_BOOT_TIMEOUT_MS,
  );
  // App-bound file-field picker capability: ONE object whose `open` has the
  // cross-cutting deps AND the browse/upload clients pre-bound at the composition
  // root — so the driver passes only field args and browse/open can't drift (it
  // can't pass a mismatched client). `browse` is the same client `open` uses, and
  // `uploadClient` is bound into `open` because the modal's Upload control needs it.
  /** @type {import('./contracts/forms.js').FilePicker} */
  const filePicker = {
    browse: fileBrowseClient,
    open: (args) => openFilePickerImpl({
      ...args,
      browseClient: fileBrowseClient,
      uploadClient,
      eventBus,
      logger,
      localeConfig,
    }),
  };
  // App-bound form-link capability: `open` carries apiClient + cross-cutting deps pre-bound, so
  // the form-link driver passes only field args and never reaches apiClient or the modal module.
  /** @type {import('./contracts/forms.js').FormPicker} */
  const formPicker = {
    open: (args) => openFormBuilderModal({ ...args, apiClient, logger }),
  };
  bootstrapApp({
    availableDesigns: AVAILABLE_DESIGNS,
    defaultDesign: 'material',
    apiClient,
    uploadClient,
    mediaClient,
    filePicker,
    formPicker,
    eventBus,
    t,
    authService,
    access,
    contextManager,
    pageRegistry,
    router,
    logger,
    contextOptions,
    fetchMenu: (/** @type {AbortSignal | undefined} */ signal) => menuAdapter.fetchMenu(signal),
    fetchAccess: (/** @type {AbortSignal | undefined} */ signal) => accessAdapter.fetchAccess(signal),
    staticBrandConfig: /** @type {any} */ (window).__ADMIN_SR_CONFIG__?.brand,
    // Company logo shown on the login screen (copied to public/assets/img/ by the build).
    loginLogoUrl: './assets/img/logo.svg',
    // The wordmark carries brand identity the generic "Admin" heading does not,
    // so give it an accessible name rather than treating it as decorative.
    loginLogoAlt: 'Franchising.pl',
    localeConfig,
  });
  logger.info('App started');
})();
